import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionToggle,
	Button,
	Content,
	ContentVariants,
	DescriptionList,
	DescriptionListDescription,
	DescriptionListGroup,
	DescriptionListTerm,
	FormGroup,
	Grid,
	GridItem,
	Split,
	SplitItem,
	Stack,
	StackItem,
} from "@patternfly/react-core";
import { PlusCircleIcon, Remove2Icon } from "@patternfly/react-icons";
import { FormMultiSelectInput } from "@rhc-shared-components/form-multi-select-component";
import { FormSelect as FormSelectInput, type ISelectItem } from "@rhc-shared-components/form-select-component";
import { FormTextInput } from "@rhc-shared-components/form-text-input";
import { RichTextEditorFormComponent } from "@rhc-shared-components/rich-text-editor";
import { FieldArray, useField } from "formik";
import type React from "react";
import { type FC, useState } from "react";
import {
	PRODUCT_FAQ_ANSWER_MAXLENGTH,
	PRODUCT_FAQ_QUESTION_MAXLENGTH,
	PRODUCT_FEATURES_DESCRIPTION_MAXLENGTH,
	PRODUCT_SEO_SEARCH_ALIAS_VALUE_MAXLENGTH,
} from "../../../constants/validation";
import { useFunctionalCategories } from "../../../queries/productDetails";
import type { FieldValue } from "./types";

interface ArrayReadOnlyProps {
	value: FieldValue | null;
	fieldKey: string;
}

interface ArrayEditableProps {
	fieldKey: string;
	values: FieldValue;
}

interface ArrayFieldConfig {
	maxItems: number;
	emptyItem: Record<string, string>;
	addLabel: string;
}

const FIELD_CONFIGS: Record<string, ArrayFieldConfig> = {
	features: { maxItems: 6, emptyItem: { title: "", description: "" }, addLabel: "Add new feature" },
	faqs: { maxItems: 4, emptyItem: { question: "", answer: "" }, addLabel: "Add new FAQ" },
	linked_resources: {
		maxItems: 8,
		emptyItem: { type: "", title: "", description: "", url: "", category: "documentation & support" },
		addLabel: "Add new resource",
	},
	contacts: {
		maxItems: 10,
		emptyItem: { email_address: "", type: "Marketing contact" },
		addLabel: "Add another contact",
	},
	search_aliases: { maxItems: 3, emptyItem: { key: "", value: "" }, addLabel: "Add new search alias" },
	functional_categories: { maxItems: 3, emptyItem: {}, addLabel: "Add category" },
};

const DOCUMENTATION_TYPE_OPTIONS: ISelectItem[] = [
	{ key: "Article", value: "Article" },
	{ key: "Documentation", value: "Documentation" },
	{ key: "Video", value: "Video" },
	{ key: "Website", value: "Website" },
	{ key: "Podcasts", value: "Podcasts" },
	{ key: "On-demand Events", value: "On-demand Events" },
];

const CONTACT_TYPE_OPTIONS: ISelectItem[] = [
	{ key: "Marketing contact", value: "Marketing contact" },
	{ key: "Technical contact", value: "Technical contact" },
];

// ─── Types ───────────────────────────────────────────────────────────

type ReadOnlyRenderer = (item: any, index: number) => React.ReactNode;
type EditableRenderer = (index: number, onRemove: () => void, canRemove: boolean) => React.ReactNode;

// ─── Read-only renderers (disabled inputs matching real forms) ───────

const FeaturesAccordionReadOnly: FC<{ items: { title?: string; description?: string }[] }> = ({ items }) => {
	const [expanded, setExpanded] = useState<string>("");

	return (
		<Accordion asDefinitionList={false}>
			{items.map((item, index) => {
				const id = `ro-feat-acc-${index}`;
				return (
					<AccordionItem isExpanded={expanded === id} key={id}>
						<AccordionToggle id={id} onClick={() => setExpanded(expanded === id ? "" : id)}>
							{item.title || `Feature ${index + 1}`}
						</AccordionToggle>
						<AccordionContent>
							{/* biome-ignore lint/security/noDangerouslySetInnerHtml: trusted API content */}
							<Content dangerouslySetInnerHTML={{ __html: item?.description ?? "" }} />
						</AccordionContent>
					</AccordionItem>
				);
			})}
		</Accordion>
	);
};

const LinkedResourcesAccordionReadOnly: FC<{
	items: { type?: string; title?: string; description?: string; url?: string }[];
}> = ({ items }) => {
	const [expanded, setExpanded] = useState<string>("");

	return (
		<Accordion asDefinitionList={false}>
			{items.map((item, index) => {
				const id = `ro-lr-acc-${index}`;
				return (
					<AccordionItem isExpanded={expanded === id} key={id}>
						<AccordionToggle id={id} onClick={() => setExpanded(expanded === id ? "" : id)}>
							{item.title || `Resource ${index + 1}`}
						</AccordionToggle>
						<AccordionContent>
							<Stack hasGutter>
								<StackItem>
									<FormGroup label="Type" fieldId={`ro-lr-type-${index}`}>
										<Content>
											<Content component={ContentVariants.p}>{item.type || ""}</Content>
										</Content>
									</FormGroup>
								</StackItem>
								<StackItem>
									<FormGroup label="Description" fieldId={`ro-lr-desc-${index}`}>
										<Content>
											<Content component={ContentVariants.p}>{item.description || ""}</Content>
										</Content>
									</FormGroup>
								</StackItem>
								<StackItem>
									<FormGroup label="Resource URL" fieldId={`ro-lr-url-${index}`}>
										<Content>
											<Content
												component={ContentVariants.a}
												href={item.url || "#"}
												target="_blank"
												rel="noopener noreferrer"
											>
												{item.url || ""}
											</Content>
										</Content>
									</FormGroup>
								</StackItem>
							</Stack>
						</AccordionContent>
					</AccordionItem>
				);
			})}
		</Accordion>
	);
};

const ContactsGroupedReadOnly: FC<{ items: { email_address?: string; type?: string }[] }> = ({ items }) => {
	const grouped = items.reduce<Record<string, string[]>>((acc, item) => {
		const type = item.type || "Contact";
		if (!acc[type]) acc[type] = [];
		if (item.email_address) acc[type].push(item.email_address);
		return acc;
	}, {});

	return (
		<Stack hasGutter>
			{Object.entries(grouped).map(([type, emails]) => (
				<StackItem key={type}>
					<FormGroup label={type} fieldId={`ro-ct-group-${type}`}>
						<Content>
							<Content component={ContentVariants.p}>{emails.join(", ")}</Content>
						</Content>
					</FormGroup>
				</StackItem>
			))}
		</Stack>
	);
};

const SearchAliasesDescriptionList: FC<{ items: { key?: string; value?: string }[] }> = ({ items }) => (
	<DescriptionList>
		{items.map((item, index) => (
			<DescriptionListGroup key={index}>
				<DescriptionListTerm>{item.key || ""}</DescriptionListTerm>
				<DescriptionListDescription>{item.value || ""}</DescriptionListDescription>
			</DescriptionListGroup>
		))}
	</DescriptionList>
);

// ─── Editable renderers (matching exact tab forms) ───────────────────

const RemoveButton: FC<{ onClick: () => void; label: string }> = ({ onClick, label }) => (
	<Button icon={<Remove2Icon color="#6A6E73" />} variant="plain" aria-label={`Remove ${label}`} onClick={onClick} />
);

const renderFeatureEditable = (index: number, onRemove: () => void, canRemove: boolean) => (
	<Stack hasGutter key={index}>
		<StackItem>
			<Split>
				<SplitItem isFilled>
					<FormTextInput
						name={`fieldValue.${index}.title`}
						label="Title"
						aria-label={`Feature ${index + 1} Title`}
						type="text"
					/>
				</SplitItem>
				{canRemove && (
					<SplitItem>
						<RemoveButton onClick={onRemove} label={`Feature ${index + 1}`} />
					</SplitItem>
				)}
			</Split>
		</StackItem>
		<StackItem>
			<RichTextEditorFormComponent
				name={`fieldValue.${index}.description`}
				label="Description"
				maxLength={PRODUCT_FEATURES_DESCRIPTION_MAXLENGTH}
				showMaxLengthText
			/>
		</StackItem>
	</Stack>
);

const renderFaqEditable = (index: number, onRemove: () => void, canRemove: boolean) => (
	<Stack hasGutter key={index}>
		<StackItem>
			<Split>
				<SplitItem isFilled>
					<FormTextInput
						name={`fieldValue.${index}.question`}
						label="Question"
						aria-label={`FAQ ${index + 1} Question`}
						type="text"
						maxLength={PRODUCT_FAQ_QUESTION_MAXLENGTH}
					/>
				</SplitItem>
				{canRemove && (
					<SplitItem>
						<RemoveButton onClick={onRemove} label={`FAQ ${index + 1}`} />
					</SplitItem>
				)}
			</Split>
		</StackItem>
		<StackItem>
			<RichTextEditorFormComponent
				name={`fieldValue.${index}.answer`}
				label="Answer"
				maxLength={PRODUCT_FAQ_ANSWER_MAXLENGTH}
				showMaxLengthText
			/>
		</StackItem>
	</Stack>
);

const renderLinkedResourceEditable = (index: number, onRemove: () => void, canRemove: boolean) => (
	<Stack hasGutter key={index}>
		<StackItem>
			<Split>
				<SplitItem isFilled>
					<Grid hasGutter>
						<GridItem span={3}>
							<FormSelectInput
								name={`fieldValue.${index}.type`}
								label="Type"
								placeholder="Choose type"
								selectOptions={DOCUMENTATION_TYPE_OPTIONS}
								isRequired
								isFullWidth
								ariaLabel={`Resource ${index + 1} Type`}
							/>
						</GridItem>
						<GridItem span={9}>
							<FormTextInput name={`fieldValue.${index}.title`} label="Title" isRequired type="text" />
						</GridItem>
					</Grid>
				</SplitItem>
				{canRemove && (
					<SplitItem>
						<RemoveButton onClick={onRemove} label={`Resource ${index + 1}`} />
					</SplitItem>
				)}
			</Split>
		</StackItem>
		<StackItem>
			<FormTextInput name={`fieldValue.${index}.description`} label="Description" isRequired type="text" />
		</StackItem>
		<StackItem>
			<FormTextInput name={`fieldValue.${index}.url`} label="Resource URL" isRequired type="text" />
		</StackItem>
	</Stack>
);

const renderContactEditable = (index: number, onRemove: () => void, canRemove: boolean) => (
	<Split key={index}>
		<SplitItem isFilled>
			<Grid hasGutter>
				<GridItem span={5}>
					<FormSelectInput
						name={`fieldValue.${index}.type`}
						label="Contact type"
						placeholder="Choose type"
						selectOptions={CONTACT_TYPE_OPTIONS}
						isRequired
						isFullWidth
					/>
				</GridItem>
				<GridItem span={7}>
					<FormTextInput name={`fieldValue.${index}.email_address`} label="Email address" isRequired type="text" />
				</GridItem>
			</Grid>
		</SplitItem>
		{canRemove && (
			<SplitItem>
				<RemoveButton onClick={onRemove} label={`Contact ${index + 1}`} />
			</SplitItem>
		)}
	</Split>
);

const renderSearchAliasEditable = (index: number, onRemove: () => void, canRemove: boolean) => (
	<Split key={index}>
		<SplitItem isFilled>
			<Grid hasGutter>
				<GridItem span={6}>
					<FormTextInput
						name={`fieldValue.${index}.key`}
						label="Key"
						type="text"
						{...(index === 0 ? { formGroupProps: { helperText: 'An example key would be "RHEL"' } } : {})}
					/>
				</GridItem>
				<GridItem span={6}>
					<FormTextInput
						name={`fieldValue.${index}.value`}
						label="Value"
						maxLength={PRODUCT_SEO_SEARCH_ALIAS_VALUE_MAXLENGTH}
						{...(index === 0
							? { formGroupProps: { helperText: 'An example value is "Red Hat Enterprise Linux"' } }
							: {})}
					/>
				</GridItem>
			</Grid>
		</SplitItem>
		{canRemove && (
			<SplitItem>
				<RemoveButton onClick={onRemove} label={`Alias ${index + 1}`} />
			</SplitItem>
		)}
	</Split>
);

// ─── FAQ accordion read-only renderer ────────────────────────────────

const FaqAccordionReadOnly: FC<{ items: { question?: string; answer?: string }[] }> = ({ items }) => {
	const [expanded, setExpanded] = useState<string>("");

	return (
		<Accordion asDefinitionList={false}>
			{items.map((item, index) => {
				const id = `ro-faq-acc-${index}`;
				return (
					<AccordionItem isExpanded={expanded === id} key={id}>
						<AccordionToggle id={id} onClick={() => setExpanded(expanded === id ? "" : id)}>
							{item.question || `FAQ ${index + 1}`}
						</AccordionToggle>
						<AccordionContent>
							{/* biome-ignore lint/security/noDangerouslySetInnerHtml: trusted API content */}
							<Content dangerouslySetInnerHTML={{ __html: item?.answer ?? "" }} />
						</AccordionContent>
					</AccordionItem>
				);
			})}
		</Accordion>
	);
};

// ─── Lookup tables ───────────────────────────────────────────────────

const READ_ONLY_RENDERERS: Record<string, ReadOnlyRenderer> = {};

const EDITABLE_RENDERERS: Record<string, EditableRenderer> = {
	features: renderFeatureEditable,
	faqs: renderFaqEditable,
	linked_resources: renderLinkedResourceEditable,
	contacts: renderContactEditable,
	search_aliases: renderSearchAliasEditable,
};

// ─── Exported components ─────────────────────────────────────────────

export const ArrayReadOnly: FC<ArrayReadOnlyProps> = ({ value, fieldKey }) => {
	if (!Array.isArray(value) || value.length === 0) {
		return (
			<Content>
				<Content component={ContentVariants.small}>No data</Content>
			</Content>
		);
	}

	if (fieldKey === "functional_categories") {
		return (
			<Content>
				<Content component={ContentVariants.p}>{(value as string[]).join(", ")}</Content>
			</Content>
		);
	}

	if (fieldKey === "features") {
		return <FeaturesAccordionReadOnly items={value as { title?: string; description?: string }[]} />;
	}

	if (fieldKey === "linked_resources") {
		return (
			<LinkedResourcesAccordionReadOnly
				items={value as { type?: string; title?: string; description?: string; url?: string }[]}
			/>
		);
	}

	if (fieldKey === "search_aliases") {
		return <SearchAliasesDescriptionList items={value as { key?: string; value?: string }[]} />;
	}

	if (fieldKey === "contacts") {
		return <ContactsGroupedReadOnly items={value as { email_address?: string; type?: string }[]} />;
	}

	if (fieldKey === "faqs") {
		return <FaqAccordionReadOnly items={value as { question?: string; answer?: string }[]} />;
	}

	const renderer = READ_ONLY_RENDERERS[fieldKey];
	if (!renderer) {
		return (
			<Content>
				<Content component={ContentVariants.small}>{JSON.stringify(value, null, 2)}</Content>
			</Content>
		);
	}

	return (
		<Stack hasGutter>
			{value.map((item, index) => (
				<StackItem key={index}>{renderer(item, index)}</StackItem>
			))}
		</Stack>
	);
};

const FunctionalCategoriesEditable: FC = () => {
	const productFunctionalCategories = useFunctionalCategories();
	const categories = productFunctionalCategories?.map((data) => ({ value: data, children: data })) || [];
	const [field] = useField("fieldValue");

	// Guard: FormMultiSelectInput requires an array value
	if (!Array.isArray(field.value)) return null;

	return (
		<FormMultiSelectInput
			isRequired
			name="fieldValue"
			label=""
			selectMenuOptions={categories}
			placeholder="Select categories"
			maxHeight="200px"
		/>
	);
};

export const ArrayEditable: FC<ArrayEditableProps> = ({ fieldKey, values }) => {
	const items = Array.isArray(values) ? values : [];
	const config = FIELD_CONFIGS[fieldKey];

	if (fieldKey === "functional_categories") {
		return <FunctionalCategoriesEditable />;
	}

	const renderer = EDITABLE_RENDERERS[fieldKey];
	if (!renderer || !config) {
		return (
			<Content>
				<Content component={ContentVariants.small}>Unsupported field type</Content>
			</Content>
		);
	}

	return (
		<FieldArray name="fieldValue">
			{(arrayHelpers) => (
				<Stack hasGutter>
					{items.map((_, index) => (
						<StackItem key={index}>{renderer(index, () => arrayHelpers.remove(index), items.length > 1)}</StackItem>
					))}
					{items.length < config.maxItems && (
						<StackItem>
							<Button
								variant="link"
								isInline
								icon={<PlusCircleIcon />}
								onClick={() => arrayHelpers.push({ ...config.emptyItem })}
							>
								{config.addLabel}
							</Button>
						</StackItem>
					)}
				</Stack>
			)}
		</FieldArray>
	);
};
